import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogTrigger } from "../ui/dialog";
import { CONTACT, BROCHURE_URL } from "../../data/site";
import { WhatsAppIcon } from "./WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE = "muplast_lead";

export const RESOURCES = {
  brochure: { label: "Company brochure", action: "Download brochure", file: BROCHURE_URL },
  "iso-9001-certificate": { label: "ISO 9001:2015 certificate", action: "Download certificate", file: "/downloads/mu-plast-iso-9001-certificate.pdf" },
  "fssc-22000-certificate": { label: "FSSC 22000 certificate", action: "Download certificate", file: "/downloads/mu-plast-fssc-22000-certificate.pdf" },
};

const EMPTY = { name: "", company: "", phone: "" };
const loadSaved = () => {
  try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(STORE) || "{}") }; } catch { return EMPTY; }
};

const startDownload = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = url.split("/").pop();
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const Field = ({ id, label, ...props }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">{label}</span>
    <input id={id} data-testid={`lead-input-${id}`} required {...props}
      className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-[15px] text-ink outline-none transition-colors focus:border-brand" />
  </label>
);

const RequestSent = ({ r, form }) => {
  const msg = `Hello MU Plast, please share your ${r.label}. Name: ${form.name}, Company: ${form.company}, Phone: ${form.phone}`;
  return (
    <div data-testid="lead-request-success" className="text-center py-2">
      <CheckCircle2 className="mx-auto h-12 w-12 text-brand" strokeWidth={1.5} />
      <p className="font-display mt-4 text-xl font-semibold text-ink">Request received</p>
      <p className="mt-2 text-sm text-slate-600">Our team will share the {r.label} with you shortly. For a faster response, send the request directly:</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <a data-testid="lead-success-whatsapp" href={`${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" className="btn bg-[#128C7E] text-white"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
        <a data-testid="lead-success-email" href={`mailto:${CONTACT.emails[0]}?subject=${encodeURIComponent(`Request: ${r.label}`)}&body=${encodeURIComponent(msg)}`} className="btn bg-navy text-white"><Mail className="h-4 w-4" /> Email</a>
      </div>
    </div>
  );
};

export const DownloadDialog = ({ resource, children }) => {
  const r = RESOURCES[resource];
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(loadSaved);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { ...form, resource, page: window.location.pathname });
      localStorage.setItem(STORE, JSON.stringify(form));
      if (r.file) {
        startDownload(r.file);
        toast.success(`Your ${r.label} download has started`);
        setOpen(false);
      } else setSent(true);
    } catch {
      toast.error("Please check your details and try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) setForm(loadSaved()); setOpen(o); if (!o) setSent(false); }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent data-testid="lead-dialog" className="rounded-3xl sm:rounded-3xl max-w-md p-8 font-body">
        {sent ? <RequestSent r={r} form={form} /> : (
          <>
            <div>
              <DialogTitle className="font-display text-2xl font-semibold text-ink">{r.action}</DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-600">{r.label} — share a few details and we'll {r.file ? "start your download right away" : "send it to you"}.</DialogDescription>
            </div>
            <form onSubmit={submit} className="mt-2 space-y-4" data-testid="lead-form">
              <Field id="name" label="Your name" value={form.name} onChange={set("name")} minLength={2} autoComplete="name" />
              <Field id="company" label="Company" value={form.company} onChange={set("company")} minLength={2} autoComplete="organization" />
              <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} pattern="[0-9+\(\)\-\s]{7,20}" title="Enter a valid phone number" autoComplete="tel" />
              <button type="submit" disabled={busy} data-testid="lead-submit-button" className="btn w-full bg-navy text-white hover:bg-navy-800 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : r.file ? <Download className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                {r.action}
              </button>
              <p className="text-xs text-slate-500">Your details are only used by MU Plast to follow up on your request.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
